import React from "react";
import { Button, Input } from "reactstrap";
import { LuCloudUpload } from "react-icons/lu";
import { detectType } from "./utils";

const ChatInput = ({
  newMessage,
  setNewMessage,
  handleInputChange,
  handleSendMessage,
  isSending,
  selectedFile,
  selectedFilePreview,
  setSelectedFile,
  setSelectedFilePreview,
  pickFile,
  fileInputRef,
  onLocalFileChange,
  handleDrop,
  editingMessage,
  handleSaveEdit,
  handleCancelEdit,
  handleUploadFile
}) => {

  // ✅ FIX: Handle sending media with text
  const handleSendWithMedia = () => {
    if (selectedFile) {
      handleUploadFile(selectedFile, newMessage);
      setNewMessage("");
      setSelectedFile(null);
      setSelectedFilePreview("");
    }
  };

  // ✅ FIX: Unified send handler
  const handleSend = () => {
    if (isSending) return;
    
    if (editingMessage) {
      handleSaveEdit();
    } else if (selectedFile) {
      handleSendWithMedia();
    } else {
      handleSendMessage();
    }
  };

  // ✅ FIX: Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ✅ FIX: Handle cancel edit properly
  const handleCancel = () => {
    handleCancelEdit();
    setNewMessage("");
    setSelectedFile(null);
    setSelectedFilePreview("");
  };

  return (
    <div
      className="p-3 border-top bg-white position-relative"
      style={{ backgroundColor: "#f0f2f5" }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      {/* Edit Mode Header - Shows "Edit message" above input */}
      {editingMessage && (
        <div className="d-flex align-items-center justify-content-between mb-2 p-2 bg-light rounded">
          <div className="d-flex align-items-center">
            <i className="ri-edit-line text-primary me-2"></i>
            <small className="text-primary fw-medium">Edit message</small>
          </div>
          <Button
            color="link"
            className="p-0 text-muted"
            onClick={handleCancel}
            style={{ fontSize: '18px' }}
            title="Cancel editing"
          >
            <i className="ri-close-line"></i>
          </Button>
        </div>
      )}

      <div className="d-flex align-items-end gap-2 justify-content-between">
        {/* File Upload Button - Hidden during edit mode */}
        {!editingMessage && (
          <Button
            color="secondary"
            onClick={() => pickFile('*/*')}
            style={{
              borderRadius: "100%",
              height: "40px",
              width: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px"
            }}
            title="Upload file"
          >
            <LuCloudUpload size={20} />
          </Button>
        )}

        {/* Empty spacer not needed in edit mode; input will take full width */}
        {editingMessage && <div style={{ width: 0, height: 0 }}></div>}

        <div className="w-100 position-relative">
          {/* File Preview - Hidden during edit mode */}
          {selectedFilePreview && !editingMessage && (
            <div style={{ position: 'relative', width: "fit-content", marginBottom: "8px" }}>
              {(() => {
                const t = detectType(selectedFile?.type || '', selectedFile?.name || '');
                if (t === 'image') return <img src={selectedFilePreview} alt="preview" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }} />;
                if (t === 'video') return <video key="preview-video" src={selectedFilePreview} preload="none" playsInline style={{ width: 150, height: 100, objectFit: 'cover', borderRadius: 8 }} />;
                if (t === 'audio') return <div style={{ width: 300, height: 50 }}><audio controls src={selectedFilePreview} style={{ width: '100%', position: 'absolute', top: 0, left: 0 }} /></div>;
                return (
                  <div
                    style={{
                      width: 300,
                      height: 50,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      padding: '0 12px',
                      gap: '10px',
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                  >
                    <i
                      className="ri-file-pdf-2-fill"
                      style={{ color: '#e74c3c', fontSize: 28 }}
                    ></i>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#333',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 230,
                      }}
                    >
                      {selectedFile?.name}
                    </span>
                  </div>
                );
              })()}
              <button
                onClick={() => { setSelectedFile(null); setSelectedFilePreview(''); }}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  border: 'none',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                aria-label="Remove"
              >
                ×
              </button>
            </div>
          )}

          {/* Message Input */}
          <Input
            id="message-input"
            type="text"
            placeholder={editingMessage ? "Edit your message..." : "Type a message..."}
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            style={{
              border: editingMessage ? "1px solid #0d6efd" : "1px solid #ddd",
              padding: editingMessage ? "10px 16px" : "12px 16px",
              backgroundColor: "#fff",
              borderRadius: editingMessage ? "12px" : "24px",
              fontSize: "14px",
              boxShadow: editingMessage ? "0 0 0 2px rgba(13, 110, 253, 0.25)" : "none"
            }}
          />
        </div>

        {/* Action Buttons (Send/Save) */}
        {editingMessage ? (
          <div className="d-flex align-items-center gap-2">
            <Button
              color="primary"
              onClick={handleSend}
              disabled={isSending || !newMessage.trim()}
              style={{ borderRadius: 8, height: 40, padding: "0 16px", minWidth: 90 }}
            >
              {isSending ? "Saving..." : "Save"}
            </Button>
          </div>
        ) : (
          <div className="d-flex align-items-center">
            <Button
              color="primary"
              onClick={handleSend}
              disabled={isSending || (!newMessage.trim() && !selectedFile)}
              style={{
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#007bff",
                border: "none"
              }}
              title={selectedFile ? "Send file with text" : "Send message"}
            >
              <i className="ri-send-plane-fill"></i>
            </Button>
          </div>
        )}

        <input ref={fileInputRef} type="file" hidden onChange={onLocalFileChange} />
      </div>

      {/* Optional overlay visual feedback */}
      <div
        id="drop-overlay"
        style={{
          position: "absolute",
          inset: 0,
          border: "2px dashed #007bff",
          borderRadius: 12,
          display: "none",
          background: "rgba(0,123,255,0.1)",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          fontWeight: 500,
          color: "#007bff",
          zIndex: 5
        }}
      >
        Drop file to upload
      </div>
    </div>
  );
};

export default ChatInput;